import { createStyles, Paper, Text, UnstyledButton } from '@mantine/core';
import { SuggestionKeyDownProps, SuggestionProps } from '@tiptap/suggestion';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

export interface MentionItem {
  id: string;
  label: string;
}

export interface MentionSuggestionListRef {
  onKeyDown: (props: SuggestionKeyDownProps) => boolean;
}

const useStyles = createStyles((theme) => ({
  item: {
    display: 'block',
    width: '100%',
    padding: '6px 10px',
    borderRadius: theme.radius.sm,
    '&[data-selected="true"], &:hover': {
      backgroundColor: theme.colors.red[0],
    },
  },
}));

/**
 * Dropdown suggestion list rendered by the TipTap Mention extension.
 * Shows context-filtered field references and handles keyboard navigation.
 */
const MentionSuggestionList = forwardRef<MentionSuggestionListRef, SuggestionProps<MentionItem>>(
  ({ items, command }, ref) => {
    const { classes } = useStyles();
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => setSelectedIndex(0), [items]);

    /** Confirms the item at the given index, inserting it into the editor. */
    const selectItem = (index: number) => {
      if (items[index]) command(items[index]);
    };

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }) => {
        if (event.key === 'ArrowUp') {
          setSelectedIndex((i) => (i + items.length - 1) % Math.max(items.length, 1));
          return true;
        }
        if (event.key === 'ArrowDown') {
          setSelectedIndex((i) => (i + 1) % Math.max(items.length, 1));
          return true;
        }
        if (event.key === 'Enter') {
          selectItem(selectedIndex);
          return true;
        }
        return false;
      },
    }));

    if (items.length === 0) {
      return (
        <Paper shadow="sm" p={8} withBorder>
          <Text size="sm" color="dimmed">
            Žiadne pole
          </Text>
        </Paper>
      );
    }

    return (
      <Paper shadow="sm" p={4} withBorder>
        {items.map((item, index) => (
          <UnstyledButton
            key={item.id}
            data-selected={index === selectedIndex}
            className={classes.item}
            onClick={() => selectItem(index)}
          >
            <Text size="sm">{item.label}</Text>
          </UnstyledButton>
        ))}
      </Paper>
    );
  }
);

MentionSuggestionList.displayName = 'MentionSuggestionList';

export default MentionSuggestionList;
